"use client";
import { getCourseCatalog } from "@/lib/courseData";
import { useIsLoggedIn } from "@/lib/hooks/useIsLoggedIn";
import { Box } from "@mui/material";
import { useLocale } from "next-intl";
import { useRouter } from "next/navigation";
import { createContext, useContext, useEffect, useState } from "react";
import CourseFAB from "./CourseFAB";
import CourseSidebar from "./CourseSidebar";

// 创建 Context
interface CourseContextType {
  catalog: any[];
  loadCatalog: () => Promise<void>;
}

const CourseContext = createContext<CourseContextType | undefined>(undefined);

// Hook 用于访问 Context
export const useCourseContext = () => {
  const context = useContext(CourseContext);
  if (context === undefined) {
    throw new Error("useCourseContext must be used within a CourseLayout");
  }
  return context;
};

interface CourseLayoutProps {
  children: React.ReactNode;
}

export default function CourseLayout({ children }: CourseLayoutProps) {
  const locale = useLocale();
  const router = useRouter();
  const [catalog, setCatalog] = useState<any[]>([]);
  const isLoggedIn = useIsLoggedIn();

  const loadCatalog = async () => {
    const catalogResult = await getCourseCatalog(locale);
    setCatalog(catalogResult.data);
  };

  // 加载课程目录
  useEffect(() => {
    if (isLoggedIn === false) {
      router.push("/auth/signup");
      return;
    }
    loadCatalog();
  }, [locale, isLoggedIn]);

  const contextValue: CourseContextType = {
    catalog,
    loadCatalog,
  };

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        minHeight: "100vh",
        maxWidth: "75rem",
        margin: "0 auto",
        color: "text.primary",
        display: "flex",
        justifyContent: "space-between",
        py: 6,
        position: "relative", // 为 FAB 按钮提供定位上下文
      }}>
      {/* 桌面端侧边栏 - 小屏幕隐藏 */}
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <CourseSidebar catalog={catalog} />
      </Box>

      {/* 主内容区域 */}
      <CourseContext.Provider value={contextValue}>
        <Box
          sx={{
            flex: 1,
            maxWidth: { xs: "100%", md: "42.875rem" },
            px: { xs: 2, md: 0 },
            ml: { xs: 0, md: 2 },
          }}>
          {children}
        </Box>
      </CourseContext.Provider>

      {/* 移动端 FAB 按钮 */}
      <CourseFAB catalog={catalog} />
    </Box>
  );
}
